import { useMutation } from '@apollo/client';
import { createError } from '@common/neo.tax';
import { ErrorEvent } from '@tryfinch/react-connect';
import { useCallback, useEffect, useState } from 'react';

import { evictCache } from '@neo.tax/shared/apollo';
import {
  CREATE_FINCH_ACCESS_TOKEN,
  CreateNewFinchAccessTokenMutation,
  CreateNewFinchAccessTokenMutationVariables,
  FinchScopeType,
  GET_SYNCED_PROVIDERS,
  SYNC_PAYROLL_PROVIDER,
  SyncPayrollProviderMutation,
  SyncPayrollProviderMutationVariables,
  GetSyncedProvidersQueryVariables,
} from '@neo.tax/shared/graphql';
import { useCurrentCompanyId, useCurrentCompany, useCurrentUser } from '@neo.tax/shared/hooks';
import {
  FinchErrorCallback,
  FinchProvider,
  FinchSuccessCallback,
  sendFSEvent,
  useFinch,
} from '@neo.tax/shared/third-parties';

import { ConnectProviderStatus } from '../enums';
import { UseProviderConnection } from '../types';
import { useProviderConnectState } from './useProviderConnectState';

type UseSyncFinchProviderProps = {
  finchSessionId: string;
  providerName: FinchProvider;
  fiscalYearId: string;
};

export const useSyncFinchProvider = ({
  finchSessionId,
  fiscalYearId,
  providerName,
}: UseSyncFinchProviderProps): UseProviderConnection => {
  const [connectStatus, setConnectStatus] = useState<ConnectProviderStatus>(ConnectProviderStatus.NOT_STARTED);
  const companyId = useCurrentCompanyId();
  const { company } = useCurrentCompany();
  const { user } = useCurrentUser();
  const { loading, payrollToken, synced, tokenRegistered } = useProviderConnectState({
    fiscalYearId,
    providerName,
    providerType: 'payroll',
  });

  const [createFinchAccessToken] = useMutation<
    CreateNewFinchAccessTokenMutation,
    CreateNewFinchAccessTokenMutationVariables
  >(CREATE_FINCH_ACCESS_TOKEN);

  const [syncPayrollProvider] = useMutation<SyncPayrollProviderMutation, SyncPayrollProviderMutationVariables>(
    SYNC_PAYROLL_PROVIDER,
    {
      awaitRefetchQueries: true,
      update: (cache) => {
        //invalidate getPayrollQres and getPayrollQreSummary cache
        evictCache(cache, ['getPayrollQres', 'getPayrollQreSummary']);
      },
      refetchQueries: [
        {
          query: GET_SYNCED_PROVIDERS,
          variables: {
            fiscalYearId,
          } satisfies GetSyncedProvidersQueryVariables,
        },
      ],
    },
  );

  const syncPayroll = useCallback(async (): Promise<void> => {
    if (!payrollToken) {
      return;
    }

    setConnectStatus(ConnectProviderStatus.SYNCING);
    try {
      await syncPayrollProvider({
        variables: {
          fiscalYearId,
          payrollProvider: providerName,
        },
      });
      sendFSEvent('payroll_sync_success', { payroll_provider_str: providerName }, user, company);

      setConnectStatus(ConnectProviderStatus.SUCCESS);
    } catch (err) {
      setConnectStatus(ConnectProviderStatus.ERROR);
      sendFSEvent(
        'payroll_sync_error',
        {
          payroll_provider_str: providerName,
          payroll_error_str: JSON.stringify(err),
        },
        user,
        company,
      );
      throw err;
    }
  }, [payrollToken, syncPayrollProvider, fiscalYearId, providerName, user, company]);

  useEffect(() => {
    if (connectStatus !== ConnectProviderStatus.WAITING_TOKEN || !payrollToken) return;

    // Skip R&D payroll sync for HR-only Finch connections (used for ASC-only companies like Red Ventures)
    if (payrollToken.finchScopeType === FinchScopeType.HrOnly) {
      setConnectStatus(ConnectProviderStatus.SUCCESS);
      return;
    }

    syncPayroll();
  }, [connectStatus, syncPayroll, payrollToken]);

  const onFinchSuccess: FinchSuccessCallback = async ({ code }: { code: string }) => {
    if (!user) {
      throw createError('MissingUser', 'Tried syncing finch without a User');
    }
    try {
      await createFinchAccessToken({
        variables: {
          userId: user.id,
          companyId,
          finchAuthCode: code,
          provider: providerName,
        },
      });
      setConnectStatus(ConnectProviderStatus.WAITING_TOKEN);
    } catch (err) {
      sendFSEvent(
        'payroll_create_token_error',
        {
          payroll_provider_str: providerName,
          payroll_error_str: JSON.stringify(err),
        },
        user,
        company,
      );
      setConnectStatus(ConnectProviderStatus.ERROR);
    }
  };

  const onFinchError: FinchErrorCallback = (response: ErrorEvent) => {
    sendFSEvent(
      'finch_error',
      {
        payroll_provider_str: providerName,
        finch_error_str: response.errorMessage,
      },
      user,
      company,
    );
    setConnectStatus(ConnectProviderStatus.ERROR);
  };

  const { open: openFinch } = useFinch(finchSessionId, onFinchSuccess, onFinchError);

  const connectFinch = (): void => {
    if (loading) return;
    if (!tokenRegistered) {
      sendFSEvent('finch_open', { payroll_provider_str: providerName }, user, company);
      openFinch();
    } else if (!synced) {
      setConnectStatus(ConnectProviderStatus.WAITING_TOKEN);
    } else {
      setConnectStatus(ConnectProviderStatus.SUCCESS);
    }
  };

  return {
    connect: connectFinch,
    connectStatus,
  };
};
